import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen } from 'lucide-react';
import { getLanguageSettings } from '@/api/publicClient';

export default function Footer({ showText = true }) {
  const { t } = useTranslation();
  const [footerSettings, setFooterSettings] = useState({
    footer_copyright: '© 2024 BookForge Publishing. Все права защищены.',
    footer_text: 'Создаем и издаем качественную литературу для думающих читателей',
    language: 'ru'
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const data = await getLanguageSettings();
        setFooterSettings({
          footer_copyright: data.footer_copyright || '© 2024 BookForge Publishing. Все права защищены.',
          footer_text: data.footer_text || 'Создаем и издаем качественную литературу для думающих читателей',
          language: data.language || 'ru'
        });
      } catch (error) {
        console.warn('Failed to fetch footer settings:', error);
        // Use default values if fetch fails
      } finally {
        setIsLoading(false);
      }
    };

    fetchFooterSettings();
  }, []);

  if (isLoading) {
    return (
      <footer className="bg-slate-900 text-white py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-4 bg-slate-700 rounded mb-2"></div>
            <div className="h-3 bg-slate-700 rounded"></div>
          </div>
        </div>
      </footer>
    );
  }

  const getLanguageName = (code) => {
    const languages = {
      'ru': 'Русский',
      'en': 'English',
      'sr': 'Српски'
    };
    return languages[code] || code;
  };

  return (
    <footer className="bg-slate-900 text-white py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Left side - Logo and text */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              {(() => {
                if (!showText) return null;
                const raw = footerSettings.footer_text || '';
                // Нормализуем переносы строк: CRLF, lone CR, а также экранированные литералы и <br>
                const normalized = raw
                  .replace(/<br\s*\/?>/gi, '\n')
                  .replace(/\r\n/g, '\n')
                  .replace(/\r/g, '\n')
                  .replace(/\\r\\n/g, '\n')
                  .replace(/\\n/g, '\n');
                if (!normalized) return null;
                return (
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{normalized}</p>
                );
              })()}
              <p className="text-xs text-slate-400 mt-1">{footerSettings.footer_copyright}</p>
            </div>
          </div>

          {/* Right side - Language display */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">{t('footer.language')}:</span>
            <span className="text-sm text-slate-300 font-medium">
              {getLanguageName(footerSettings.language)}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
